import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

/**
 * Hook that checks for pending subscriptions and redirects to pricing
 * This ensures the redirect happens in the correct tab (where user lands after email confirmation)
 *
 * IMPORTANT: Only checks when the URL contains email confirmation markers to prevent
 * the original signup tab from stealing the redirect from the email confirmation tab.
 */
export function usePendingSubscription() {
  const { user } = useAuth();
  const hasChecked = useRef(false);

  useEffect(() => {
    // Only check once per mount and only if user is authenticated
    if (!user || hasChecked.current) return;

    // CRITICAL: Only check for pending subscriptions if this tab came from an email confirmation
    // This prevents the original signup tab (Tab A) from checking and clearing the pending subscription
    // before the email confirmation tab (Tab B) has a chance to process it.
    // We use a custom query parameter added to emailRedirectTo in AuthContext.tsx
    const searchParams = new URLSearchParams(window.location.search);
    const isEmailConfirmationTab = searchParams.get('source') === 'email-confirmation';

    // If this isn't an email confirmation tab, don't check for pending subscriptions
    if (!isEmailConfirmationTab) {
      logger.debug('[usePendingSubscription] Not an email confirmation tab, skipping check');
      return;
    }

    logger.info('[usePendingSubscription] Email confirmation detected, checking for pending subscription...');

    const checkPendingSubscription = async () => {
      try {
        // Check for pending subscription in database
        // Use .maybeSingle() instead of .single() to handle 0 rows gracefully
        const { data: pendingSubscription, error } = await supabase
          .from('pending_subscriptions')
          .select('plan, interval')
          .eq('user_id', user.id)
          .is('claimed_at', null)
          .maybeSingle(); // Returns null if no rows, doesn't throw error

        // Only proceed if we found a pending subscription
        if (!error && pendingSubscription) {
          logger.info('[usePendingSubscription] Found pending subscription, redirecting...');

          // Mark as claimed to prevent multiple redirects
          await supabase
            .from('pending_subscriptions')
            .update({ claimed_at: new Date().toISOString() })
            .eq('user_id', user.id);

          // Redirect to pricing page with subscription params
          // This happens in the tab where the user is actually looking
          window.location.href = `/virtual-staging/pricing?plan=${pendingSubscription.plan}&interval=${pendingSubscription.interval}`;
        } else if (error) {
          // Only log actual errors, not "no rows found"
          logger.error('[usePendingSubscription] Error checking pending subscription:', { error });
        } else {
          // No pending subscription found, clean up the URL hash
          // This keeps the URL clean after email confirmation
          logger.debug('[usePendingSubscription] No pending subscription found, cleaning URL');
          if (window.location.hash) {
            // Remove the hash fragment to clean up the URL
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
      } catch (error) {
        logger.error('[usePendingSubscription] Unexpected error:', { error });
      }
    };

    // Mark as checked to prevent multiple checks
    hasChecked.current = true;
    checkPendingSubscription();
  }, [user]);
}